import type { ApiClient } from "../api/client.js";
import { ApiError } from "../api/client.js";

/**
 * Turning job ids into the references people actually say out loud.
 *
 * A task carries `job_id` and nothing else, so a task list either shows a uuid —
 * which nobody has ever read aloud in a site meeting — or reads the job back.
 * There is no batch route, so this is one `GET /api/jobs/:id` per id, settled
 * individually: a stale id answering 404 must not empty a column, and a role
 * that cannot read a particular job must not lose the task attached to it.
 *
 * Distinct from `client.jobs()`, which is about DISCOVERING which jobs exist
 * at all (`Pipeline` uses that one). This one is given the ids and only
 * resolves them — the job picker in `Tasks`' "add a task" form still only
 * knows the jobs this browser has been given, not the organisation's whole
 * list, so it still needs this rather than `client.jobs()` directly.
 */

export interface JobReferences {
  /** Job id to reference, for every id that resolved. */
  readonly references: ReadonlyMap<string, string>;
  /** How many ids did not resolve, so a screen can say so rather than imply zero. */
  readonly unresolved: number;
}

export async function resolveJobReferences(
  client: ApiClient,
  ids: Iterable<string>,
): Promise<JobReferences> {
  const unique = [...new Set(ids)];
  const settled = await Promise.allSettled(unique.map((id) => client.getJob(id)));

  const references = new Map<string, string>();
  let unresolved = 0;
  for (const [index, outcome] of settled.entries()) {
    if (outcome.status === "fulfilled") {
      references.set(unique[index]!, outcome.value.reference);
    } else if (outcome.reason instanceof ApiError) {
      // A 404 (gone, or another organisation's) and a 403 (not yours to read)
      // are the same thing here: no reference to show. The difference matters
      // on a job screen, not in a column beside a task title.
      unresolved++;
    } else {
      unresolved++;
    }
  }

  return { references, unresolved };
}
